import {
  FunctionField,
  ReferenceArrayInput,
  ReferenceField,
  ReferenceInput,
  useRecordContext,
  useResourceDefinition,
} from "react-admin";
import AutocompleteInputFluid from "./autocompleteInputFluid";
import { WithPermissions } from "../acl/withPermissions";
import { Box } from "@mui/material";
import { Debug } from "./debug";
import get from "lodash/get";
import { CopyToClipboardButton } from "./copyToClipboardButton";
import { asArray, humanize } from "../utils/tools";
import { MySingleFieldList } from "./mySingleFieldList";
import { PreloadedManyField } from "./preloadedManyField";
import { Edit, Visibility } from "@mui/icons-material";
import { RecordTooltip } from "./recordTooltip";
import { getValidResource } from "../acl/tools";

const Label = ({ value, field }) => {
  const prefix = field === "id" ? "#" : "";
  return prefix + value;
};

const PrimaryLabel = ({ record, primaryField, formatLabel }) => {
  let label =
    asArray(primaryField)
      .map((field) => get(record, field))
      .filter((v) => v)[0] || "unknown";

  if (formatLabel) {
    label = formatLabel(label, record);
  }

  return (
    <Box className="MyReferencePrimaryLabel">
      <Label value={label} field={primaryField} />
    </Box>
  );
};

const SecondaryLabel = ({ record, secondaryField }) => {
  if (!secondaryField) return null;

  const label = record[secondaryField];

  return (
    <Box className="MyReferenceSecondaryLabel">
      {" "}
      (<Label value={label} field={secondaryField} />)
    </Box>
  );
};

const LinkIcon = ({ reference, link }) => {
  const resourceDefinition = useResourceDefinition({ resource: reference });

  if (!link) return null;

  const Icon = link === "edit" ? Edit : Visibility;
  return (link === "edit" && !resourceDefinition.hasEdit) ||
    (link === "show" && !resourceDefinition.hasShow) ? null : (
    <Icon sx={{ verticalAlign: "middle", marginLeft: "5px" }} />
  );
};

const DefaultLabel = (
  record,
  {
    primaryField,
    secondaryField,
    render,
    formatLabel = humanize,
    reference,
    link,
  }
) => {
  const debugInfo = [primaryField, secondaryField].includes("id") ? null : (
    <Debug sx={{ display: "block" }}>
      {" "}
      (#{record.id})
      <CopyToClipboardButton
        data={record.id}
        sx={{ backgroundColor: "transparent" }}
      />
    </Debug>
  );

  const label = (
    <Box className="MyReferenceLabel" sx={{ " *": { display: "inline" } }}>
      <PrimaryLabel
        record={record}
        primaryField={primaryField}
        formatLabel={formatLabel}
      />
      <SecondaryLabel record={record} secondaryField={secondaryField} />
      <RecordTooltip record={record}>
        <LinkIcon reference={reference} link={link} />
      </RecordTooltip>
      {debugInfo}
    </Box>
  );

  return render ? render(record, label) : label;
};

const defaults = {
  primaryField: "name",
};

export const MyReferenceInput = ({
  can,
  multiple,
  defaultValue,
  target = "id",
  formatLabel = humanize,
  ...props
}) => {
  const filter = (searchText) => ({ [props.primaryField]: `%${searchText}` });
  const Label = (choice) => DefaultLabel(choice, props);
  const inputText = (choice) => {
    let text = choice[props.primaryField];
    if (formatLabel) {
      text = formatLabel(text);
    }

    if (props.secondaryField) {
      text += " (" + choice[props.secondaryField] + ")";
    }

    return text;
  };

  const Component = multiple ? ReferenceArrayInput : ReferenceInput;

  return (
    <WithPermissions
      label="MyReferenceInput"
      can={can || { resource: props.reference, action: "list" }}
    >
      <Component {...props} queryOptions={{ meta: { target } }}>
        <AutocompleteInputFluid
          filterToQuery={filter}
          optionText={Label}
          optionValue={target}
          label={props.label}
          slack={props.slack}
          disabled={props.disabled}
          helperText={props.helperText}
          inputText={inputText}
          multiple={multiple}
          defaultValue={defaultValue ?? multiple ? [] : null}
          getOptionDisabled={props.getOptionDisabled}
        />
      </Component>
    </WithPermissions>
  );
};

MyReferenceInput.defaultProps = defaults;

export const MyReferenceField = ({
  className,
  style,
  emptyReference,
  ...props
}) => {
  const record = props.record || useRecordContext();
  if (!record) return null;

  const referenceValue = get(record, props.source);
  const missingReferenceText = referenceValue ? String(referenceValue) : null;
  const NoPermission = <Box>#{missingReferenceText}</Box>;
  const EmptyReference =
    !referenceValue &&
    (emptyReference || <Debug>Empty Reference ({props.source})</Debug>);

  props.reference = getValidResource(props.reference);

  return (
    <WithPermissions
      label="MyReferenceField"
      can={{ resource: props.reference, action: "list" }}
      fail={NoPermission}
    >
      {EmptyReference || (
        <ReferenceField emptyText={missingReferenceText} {...props}>
          <FunctionField
            render={(record) => DefaultLabel(record, props)}
            {...{ className, style }}
          />
        </ReferenceField>
      )}
    </WithPermissions>
  );
};

MyReferenceField.defaultProps = { ...defaults, link: "show" };

export const MyReferencesField = ({
  render,
  primaryField,
  secondaryField,
  link,
  emptyReference,
  noReferences,
  linkResource,
  ...props
}) => {
  const record = props.record || useRecordContext();
  if (!record) return null;

  const referenceValue = get(record, props.source);
  const EmptyReference =
    !referenceValue &&
    (emptyReference || <Debug>Empty Reference ({props.source})</Debug>);

  return (
    <WithPermissions
      label="MyReferencesField"
      can={{ resource: props.reference, action: "list" }}
    >
      {EmptyReference || (
        <PreloadedManyField
          {...props}
          render={(data) => (
            <MySingleFieldList
              linkType={link}
              noReferences={noReferences}
              data={data}
              resource={linkResource || props.reference}
            >
              <FunctionField
                render={(record) =>
                  DefaultLabel(record, { primaryField, secondaryField, render })
                }
              />
            </MySingleFieldList>
          )}
        />
      )}
    </WithPermissions>
  );
};

MyReferencesField.defaultProps = { ...defaults, link: "show", target: "id" };
